(() => {
  'use strict';

  const AP_VERSION = '2026-09-10-easify-options-custom-placeholder';

  /*
   * Aus-PRINTzip
   * Easify / Konva Front- & Rückseitensteuerung
   *
   * Dateinamen:
   * <produkt>-<farbe>-front.jpg
   * <produkt>-<farbe>-back.jpg
   *
   * Aktuelle Easify-Layer-Zuordnung:
   * nodeIndex 0 = Logo / Motiv Vorderseite
   * nodeIndex 1 = Logo / Motiv Rückseite
   * nodeIndex 2 = Text (ein einziges Textfeld)
   *
   * Neue Options-Logik:
   * - Bedrucken lassen? -> Ohne Druck / Vorderseite / Rückseite / Vorderseite & Rückseite
   * - Text hinzufügen? -> Ja / Nein
   * - Textposition -> nur bei Vorderseite & Rückseite + Text = Ja
   */

  const AP = {
    view: 'front',
    userView: 'front',
    generating: false,
    allowSubmit: false,
    forcingBackground: false,
    updateTimer: null,
    easifyOptionsWatcher: null,
    lastEasifyOptionsValue: null
  };

  const AP_IMAGE_CACHE = new Map();

  const AP_CUSTOM_PLACEHOLDER =
    'https://cdn.shopify.com/s/files/1/0728/9634/8488/files/Aus-Printzip_Platzhalter.jpg?v=1789045184';

  const AP_EASIFY_DEFAULT_PLACEHOLDER = 'default_image_url.png';

  function normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/&/g, '-')
      .replace(/\+/g, '-')
      .replace(/\//g, '-')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function wait(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  function getProductInfo() {
    return document.querySelector('product-info[data-product-id]');
  }

  function getStage() {
    if (!window.Konva?.stages?.length) {
      return null;
    }

    const easifyStage = window.Konva.stages.find(stage => {
      try {
        const container = stage.container();
        return !!container?.closest?.('.tpo_canvas_builder');
      } catch (e) {
        return false;
      }
    });

    return easifyStage || window.Konva.stages[0] || null;
  }

  function getKonvaParts() {
    const stage = getStage();
    if (!stage) return null;

    const background = stage.findOne('#tpo_custom_background_image');
    const liveLayer = stage.findOne('#tpo_live_preview_layer');
    const transformerLayer = stage.findOne('#tpo_transformer_layer');

    if (!background || !liveLayer) {
      return null;
    }

    const liveNodes = liveLayer.getChildren();

    const getNodeByIndex = index =>
      liveNodes.find(node => Number(node.getAttr('nodeIndex')) === index) || null;

    return {
      stage,
      background,
      liveLayer,
      transformerLayer,
      liveNodes,
      frontImage: getNodeByIndex(0),
      backImage: getNodeByIndex(1),
      textNode: getNodeByIndex(2)
    };
  }


  function getNodeImageSrc(node) {
    return String(
      node?.image?.()?.src || ''
    );
  }

  function isEasifyDefaultPlaceholder(node) {
    if (!node || node.getClassName() !== 'Image') {
      return false;
    }

    return getNodeImageSrc(node)
      .toLowerCase()
      .includes(AP_EASIFY_DEFAULT_PLACEHOLDER);
  }

  function isCustomPlaceholder(node) {
    if (!node || node.getClassName() !== 'Image') {
      return false;
    }

    const current = getNodeImageSrc(node)
      .split('?')[0]
      .toLowerCase();

    const wanted = AP_CUSTOM_PLACEHOLDER
      .split('?')[0]
      .toLowerCase();

    return current === wanted;
  }

  function setCustomPlaceholder(node) {
    if (!node) {
      return;
    }

    if (!isEasifyDefaultPlaceholder(node)) {
      return;
    }

    if (node.getAttr('_ap_placeholder_loading')) {
      return;
    }

    node.setAttr('_ap_placeholder_loading', true);

    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      node.setAttr('_ap_placeholder_loading', false);

      /*
       * Wichtig:
       * Während das eigene Platzhalterbild geladen wurde, könnte der Kunde
       * bereits ein echtes Motiv hochgeladen haben. Dann nichts überschreiben.
       */
      if (!isEasifyDefaultPlaceholder(node)) {
        return;
      }

      node.image(image);
      node.getLayer()?.batchDraw();

      console.log(
        '[Aus-PRINTzip] Eigener Motiv-Platzhalter gesetzt:',
        node.getAttr('nodeIndex')
      );
    };

    image.onerror = () => {
      node.setAttr('_ap_placeholder_loading', false);

      console.warn(
        '[Aus-PRINTzip] Eigener Motiv-Platzhalter konnte nicht geladen werden:',
        AP_CUSTOM_PLACEHOLDER
      );
    };

    image.src = AP_CUSTOM_PLACEHOLDER;
  }

  function applyCustomMotifPlaceholders() {
    const parts = getKonvaParts();
    if (!parts) return;

    /*
     * nodeIndex 0 = Vorderseite
     * nodeIndex 1 = Rückseite
     *
     * Nur Easifys Standardbild wird ersetzt.
     * Kundenmotive bleiben unangetastet.
     */
    setCustomPlaceholder(parts.frontImage);
    setCustomPlaceholder(parts.backImage);
  }

  function installMotifPlaceholderGuards() {
    const parts = getKonvaParts();
    if (!parts) return;

    [
      parts.frontImage,
      parts.backImage
    ]
      .filter(Boolean)
      .forEach(node => {
        if (node.getAttr('_ap_placeholder_guard')) {
          return;
        }

        node.setAttr('_ap_placeholder_guard', true);

        node.on(
          'imageChange.apPrintzipPlaceholder',
          () => {
            /*
             * Easify kann den Node beim Umschalten oder Entfernen eines Uploads
             * wieder auf default_image_url.png setzen.
             * Nur in diesem Fall kommt unser eigener Platzhalter zurück.
             */
            if (isEasifyDefaultPlaceholder(node)) {
              setTimeout(
                () => setCustomPlaceholder(node),
                20
              );
            }
          }
        );
      });
  }

  function getSelectedColor() {
    const productInfo = getProductInfo();
    if (!productInfo) return '';

    const fieldsets = productInfo.querySelectorAll('fieldset');

    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend');
      if (!legend) continue;

      const legendText = normalize(legend.textContent);

      if (!legendText.includes('farbe') && !legendText.includes('color')) {
        continue;
      }

      const checked = fieldset.querySelector('input[type="radio"]:checked');

      if (checked?.value) {
        return normalize(checked.value);
      }
    }

    const labels = productInfo.querySelectorAll('.form__label, legend, label');

    for (const element of labels) {
      const text = String(element.textContent || '').trim();
      const match = text.match(/Farbe\s*:\s*([^\n]+)/i);

      if (match?.[1]) {
        return normalize(match[1]);
      }
    }

    return '';
  }

  function getControlContext(control) {
    const parts = [];

    const pushText = value => {
      const text = String(value || '').trim();
      if (!text) return;
      if (text.length > 700) return;
      parts.push(text);
    };

    if (control.id) {
      try {
        document
          .querySelectorAll(`label[for="${CSS.escape(control.id)}"]`)
          .forEach(label => pushText(label.textContent));
      } catch (e) {
        // ignore
      }
    }

    const closestLabel = control.closest('label');
    if (closestLabel) {
      pushText(closestLabel.textContent);
    }

    const fieldset = control.closest('fieldset');
    if (fieldset) {
      pushText(fieldset.querySelector('legend')?.textContent);
    }

    let prev = control.previousElementSibling;
    for (let i = 0; i < 3 && prev; i++) {
      pushText(prev.textContent);
      prev = prev.previousElementSibling;
    }

    let parent = control.parentElement;
    for (let i = 0; i < 5 && parent; i++) {
      pushText(parent.textContent);
      parent = parent.parentElement;
    }

    return normalize(parts.join(' '));
  }

  function getControlValue(control) {
    if (!control) return '';

    if (control instanceof HTMLSelectElement) {
      const option = control.options[control.selectedIndex];
      return String(option?.textContent || control.value || '').trim();
    }

    if (control instanceof HTMLInputElement) {
      return String(control.value || '').trim();
    }

    return String(control.value || '').trim();
  }

  function collectOptionCandidates() {
    const root = getProductInfo() || document;
    const items = [];

    root.querySelectorAll('select').forEach(select => {
      items.push({
        type: 'select',
        control: select,
        value: getControlValue(select),
        context: getControlContext(select)
      });
    });

    root.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
      items.push({
        type: 'radio',
        control: radio,
        value: getControlValue(radio),
        context: getControlContext(radio)
      });
    });

    return items;
  }

  function findOptionCandidatesByContext(keywords) {
    return collectOptionCandidates().filter(item =>
      keywords.some(keyword => item.context.includes(normalize(keyword)))
    );
  }

  function getEasifyCustomOptions() {
    const input =
      document.querySelector('#_tpo_custom_options') ||
      document.querySelector('input[name="properties[_tpo_custom_options]"]');

    if (!input?.value) {
      return {};
    }

    try {
      const parsed = JSON.parse(input.value);
      return parsed && typeof parsed === 'object'
        ? parsed
        : {};
    } catch (error) {
      console.warn(
        '[Aus-PRINTzip] Easify Custom Options konnten nicht gelesen werden.',
        error
      );
      return {};
    }
  }

  function getEasifyOptionValue(keyFragments) {
    const options = getEasifyCustomOptions();
    const fragments = keyFragments.map(normalize);

    for (const [key, rawValue] of Object.entries(options)) {
      const normalizedKey = normalize(key);

      if (
        fragments.some(fragment =>
          normalizedKey.includes(fragment)
        )
      ) {
        if (Array.isArray(rawValue)) {
          return rawValue.join(' ');
        }

        if (
          rawValue &&
          typeof rawValue === 'object'
        ) {
          return JSON.stringify(rawValue);
        }

        return String(rawValue ?? '');
      }
    }

    return '';
  }

  function getPrintMode() {
    /*
     * WICHTIG:
     * Easify rendert sein Dropdown nicht als echtes <select>.
     * Der zuverlässige Wert liegt in:
     * properties[_tpo_custom_options]
     *
     * Aktuell wurde dort z. B. gefunden:
     * "Druckpostion:": "Vorderseite & Rückseite [ +€18,00 ]"
     *
     * "Druckpostion" ist in Easify aktuell sogar ohne zweites "i"
     * geschrieben. Deshalb akzeptieren wir beide Schreibweisen.
     */
    const easifyValue = getEasifyOptionValue([
      'druckpostion',
      'druckposition',
      'bedrucken lassen'
    ]);

    if (easifyValue) {
      const value = normalize(easifyValue);

      if (value.includes('ohne-druck')) {
        return 'none';
      }

      if (
        value.includes('vorderseite') &&
        value.includes('rueckseite')
      ) {
        return 'both';
      }

      if (value.includes('rueckseite')) {
        return 'back';
      }

      if (value.includes('vorderseite')) {
        return 'front';
      }
    }

    /*
     * Fallback für eine eventuelle spätere Änderung
     * der Easify-Ausgabe.
     */
    const candidates = findOptionCandidatesByContext([
      'bedrucken lassen',
      'druckposition',
      'druckpostion'
    ]);

    for (const item of candidates) {
      const value = normalize(item.value);

      if (value.includes('ohne-druck')) {
        return 'none';
      }

      if (
        value.includes('vorderseite') &&
        value.includes('rueckseite')
      ) {
        return 'both';
      }

      if (value.includes('rueckseite')) {
        return 'back';
      }

      if (value.includes('vorderseite')) {
        return 'front';
      }
    }

    return '';
  }

  function getLegacyDesignMode() {
    const candidates = findOptionCandidatesByContext([
      'gestaltungsart',
      'designart',
      'design mode',
      'designmode'
    ]);

    for (const item of candidates) {
      const value = normalize(item.value);

      if (
        value.includes('logo-text') ||
        value.includes('logo-motiv-text') ||
        (value.includes('logo') && value.includes('text'))
      ) {
        return 'logo-text';
      }

      if (value.includes('text')) {
        return 'text';
      }

      if (value.includes('logo') || value.includes('motiv')) {
        return 'logo';
      }
    }

    return '';
  }

  function isTextEnabled() {
    const easifyValue = getEasifyOptionValue([
      'text hinzufuegen',
      'text-hinzufuegen',
      'text hinzufügen'
    ]);

    if (easifyValue) {
      const value = normalize(easifyValue);

      if (value.includes('nein')) {
        return false;
      }

      if (value.includes('ja')) {
        return true;
      }
    }

    const candidates = findOptionCandidatesByContext([
      'text hinzufuegen',
      'text-hinzufuegen',
      'text hinzufügen'
    ]);

    for (const item of candidates) {
      const value = normalize(item.value);

      if (value.includes('nein')) {
        return false;
      }

      if (value.includes('ja')) {
        return true;
      }
    }

    const legacy = getLegacyDesignMode();

    return (
      legacy === 'logo-text' ||
      legacy === 'text'
    );
  }

  function getTextPosition() {
    const mode = getPrintMode();

    /*
     * Bei einseitigem Druck ist die Textposition eindeutig.
     * Das Easify-Feld "Textposition" wird dort gar nicht benötigt.
     */
    if (mode === 'front') {
      return 'front';
    }

    if (mode === 'back') {
      return 'back';
    }

    if (!isTextEnabled()) {
      return '';
    }

    /*
     * Nur bei Vorderseite & Rückseite lesen wir die
     * zusätzliche Easify-Auswahl "Textposition".
     */
    const easifyValue = getEasifyOptionValue([
      'textposition',
      'text position'
    ]);

    if (easifyValue) {
      const value = normalize(easifyValue);

      if (value.includes('rueckseite')) {
        return 'back';
      }

      if (value.includes('vorderseite')) {
        return 'front';
      }
    }

    const candidates = findOptionCandidatesByContext([
      'textposition',
      'text-position',
      'text position'
    ]);

    for (const item of candidates) {
      const value = normalize(item.value);

      if (value.includes('rueckseite')) {
        return 'back';
      }

      if (value.includes('vorderseite')) {
        return 'front';
      }
    }

    /*
     * Falls Easify das Feld gerade neu rendert:
     * neutraler Fallback auf Vorderseite.
     */
    return 'front';
  }

  function clearProductImageCache() {
    AP_IMAGE_CACHE.clear();
  }

  function findProductImage(color, view, logResult = false) {
    const normalizedColor = normalize(color);
    const normalizedView = normalize(view);
    const cacheKey = `${normalizedColor}:${normalizedView}`;

    const cached = AP_IMAGE_CACHE.get(cacheKey);
    if (cached) {
      if (logResult) {
        console.log('[Aus-PRINTzip] Bild gefunden:', normalizedColor, normalizedView, cached);
      }
      return cached;
    }

    const wanted = `-${normalizedColor}-${normalizedView}`;
    const images = [...document.querySelectorAll('img')];

    for (const img of images) {
      const urls = [img.currentSrc, img.src, img.getAttribute('src')].filter(Boolean);

      for (const url of urls) {
        const decoded = decodeURIComponent(url).toLowerCase();

        if (decoded.includes('/cdn/shop/files/') && decoded.includes(wanted)) {
          AP_IMAGE_CACHE.set(cacheKey, url);

          if (logResult) {
            console.log('[Aus-PRINTzip] Bild gefunden:', normalizedColor, normalizedView, url);
          }

          return url;
        }
      }
    }

    if (logResult) {
      console.warn('[Aus-PRINTzip] Kein Bild gefunden:', normalizedColor, normalizedView);
    }

    return '';
  }

  function getWantedBackgroundUrl() {
    const color = getSelectedColor();
    if (!color) return '';
    return findProductImage(color, AP.view);
  }

  function hideBackGalleryImages() {
  const galleries = document.querySelectorAll(
    'media-gallery, [id^="GalleryViewer-"]'
  );

  galleries.forEach(gallery => {
    gallery.querySelectorAll('img').forEach(img => {
      const urls = [
        img.currentSrc,
        img.src,
        img.getAttribute('src'),
        img.getAttribute('srcset'),
        img.closest('picture')?.querySelector('source')?.getAttribute('srcset')
      ].filter(Boolean);

      const altText = normalize(
        img.getAttribute('alt') ||
        img.closest('[data-alt]')?.getAttribute('data-alt') ||
        ''
      );

      const isBackImage =
        altText.includes('rueckseite') ||
        urls.some(url => {
          try {
            return decodeURIComponent(url)
              .toLowerCase()
              .includes('-back');
          } catch (error) {
            return String(url)
              .toLowerCase()
              .includes('-back');
          }
        });

      if (!isBackImage) {
        return;
      }

      const mediaItem =
        img.closest('[data-media-id]') ||
        img.closest('.product__media-item') ||
        img.closest('.thumbnail-list__item') ||
        img.closest('li');

      if (mediaItem) {
        mediaItem.style.setProperty('display', 'none', 'important');
        mediaItem.setAttribute(
          'data-ap-hidden-back-image',
          'true'
        );
      }
    });
  });
}

  function loadBackground(url) {
    return new Promise((resolve, reject) => {
      const parts = getKonvaParts();

      if (!parts || !url) {
        reject(new Error('Kein Hintergrund verfügbar.'));
        return;
      }

      const current = parts.background.image()?.src || '';
      if (current && current.split('?')[0] === url.split('?')[0]) {
        resolve();
        return;
      }

      const image = new Image();
      image.crossOrigin = 'anonymous';

      image.onload = () => {
        AP.forcingBackground = true;

        parts.background.image(image);
        parts.background.getLayer()?.batchDraw();

        AP.forcingBackground = false;

        console.log('[Aus-PRINTzip] Hintergrund aktiv:', image.src);
        resolve();
      };

      image.onerror = () => {
        reject(new Error(`Bild konnte nicht geladen werden: ${url}`));
      };

      image.src = url;
    });
  }

  function installBackgroundGuard() {
    const parts = getKonvaParts();
    if (!parts) return;

    const bg = parts.background;

    if (bg.getAttr('_ap_background_guard')) {
      return;
    }

    bg.setAttr('_ap_background_guard', true);

    bg.on('imageChange.apPrintzip', () => {
      if (AP.forcingBackground) {
        return;
      }

      const wanted = getWantedBackgroundUrl();
      if (!wanted) return;

      const current = bg.image()?.src || '';
      const currentClean = current.split('?')[0];
      const wantedClean = wanted.split('?')[0];

      if (currentClean === wantedClean) {
        return;
      }

      console.log(
        '[Aus-PRINTzip] Easify wollte Hintergrund überschreiben.',
        '\nAktuell:',
        current,
        '\nSoll:',
        wanted
      );

      setTimeout(() => {
        if (!AP.forcingBackground) {
          loadBackground(wanted).catch(console.error);
        }
      }, 20);
    });

    console.log('[Aus-PRINTzip] Background-Guard aktiv.');
  }

  function showOnlyMotif(view) {
    const parts = getKonvaParts();
    if (!parts) return;

    const mode = getPrintMode();
    const textEnabled = isTextEnabled();
    const textSide = getTextPosition();

    const showFrontSide = view === 'front' && ['front', 'both'].includes(mode);
    const showBackSide = view === 'back' && ['back', 'both'].includes(mode);
    const showText =
      textEnabled &&
      ((showFrontSide && textSide === 'front') || (showBackSide && textSide === 'back'));

    if (parts.frontImage) {
      parts.frontImage.visible(showFrontSide);
    }

    if (parts.backImage) {
      parts.backImage.visible(showBackSide);
    }

    if (parts.textNode) {
      parts.textNode.visible(showText);
    }

    parts.liveLayer.batchDraw();
  }

  async function setView(view, remember = true) {
    if (!['front', 'back'].includes(view)) {
      return false;
    }

    AP.view = view;

    if (remember && !AP.generating) {
      AP.userView = view;
    }

    const color = getSelectedColor();
    if (!color) {
      return false;
    }

    const url = findProductImage(color, view);
    if (!url) {
      return false;
    }

    showOnlyMotif(view);
    await loadBackground(url);
    await wait(50);

    const parts = getKonvaParts();
    const current = parts?.background?.image()?.src || '';

    if (current.split('?')[0] !== url.split('?')[0]) {
      await loadBackground(url);
    }

    showOnlyMotif(view);
    updateSwitcher();

    return true;
  }

  function injectStyles() {
    if (document.getElementById('ap-view-switcher-css')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'ap-view-switcher-css';

    style.textContent = `
      .ap-product-view-switcher {
        display: grid;
        grid-template-columns: 1fr 1fr;
        width: min(430px, calc(100% - 24px));
        margin: 18px auto 14px;
        padding: 5px;
        gap: 4px;
        border: 1px solid #d5e3da;
        border-radius: 999px;
        background: #f2f7f4;
      }

      .ap-product-view-switcher[hidden] {
        display: none !important;
      }

      .ap-product-view-switcher__button {
        width: 100%;
        min-height: 46px;
        padding: 11px 22px;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: #294f41;
        font: inherit;
        font-size: 14px;
        font-weight: 750;
        line-height: 1.2;
        cursor: pointer;
      }

      .ap-product-view-switcher__button.is-active {
        background: #173d2c;
        color: #ffffff;
      }

      .ap-product-view-switcher--mobile {
        display: none;
      }

      .ap-preview-generating {
        opacity: .65 !important;
        pointer-events: none !important;
      }

      @media screen and (max-width: 749px) {
        .ap-product-view-switcher:not(.ap-product-view-switcher--mobile) {
          display: none !important;
        }

        .ap-product-view-switcher--mobile {
          display: grid;
          grid-template-columns: 1fr 1fr;
          width: calc(100% - 20px);
          max-width: 390px;
          margin: 14px auto 16px;
          padding: 5px;
          gap: 4px;
          border: 1px solid #d5e3da;
          border-radius: 999px;
          background: #f2f7f4;
        }

        .ap-product-view-switcher--mobile[hidden] {
          display: none !important;
        }

        .ap-product-view-switcher--mobile .ap-product-view-switcher__button {
          width: 100%;
          min-height: 44px;
          padding: 10px 12px;
          font-size: 13px;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function ensureSwitcher() {
    let switcher = document.querySelector(
      '.ap-product-view-switcher:not(.ap-product-view-switcher--mobile)'
    );

    const builders = [...document.querySelectorAll('.tpo_canvas_builder')];
    if (!builders.length) {
      return null;
    }

    const builder =
      builders.find(element => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);

        return (
          rect.width > 0 &&
          rect.height > 0 &&
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        );
      }) || builders[0];

    if (!switcher) {
      switcher = document.createElement('div');
      switcher.className = 'ap-product-view-switcher';

      switcher.innerHTML = `
        <button type="button" class="ap-product-view-switcher__button" data-ap-view="front" aria-pressed="true">
          Vorderseite
        </button>
        <button type="button" class="ap-product-view-switcher__button" data-ap-view="back" aria-pressed="false">
          Rückseite
        </button>
      `;

      switcher.addEventListener('click', async event => {
        const button = event.target.closest('[data-ap-view]');
        if (!button || AP.generating) {
          return;
        }

        await setView(button.dataset.apView, true);
      });
    }

    if (switcher.previousElementSibling !== builder) {
      builder.insertAdjacentElement('afterend', switcher);
    }

    return switcher;
  }

  function ensureMobileSwitcher() {
    let switcher = document.querySelector('.ap-product-view-switcher--mobile');
    if (switcher) {
      return switcher;
    }

    const productInfo = getProductInfo();
    if (!productInfo) {
      return null;
    }

    const galleryViewer = productInfo.querySelector('[id^="GalleryViewer-"]');
    if (!galleryViewer) {
      return null;
    }

    switcher = document.createElement('div');
    switcher.className = 'ap-product-view-switcher ap-product-view-switcher--mobile';

    switcher.innerHTML = `
      <button type="button" class="ap-product-view-switcher__button" data-ap-view="front" aria-pressed="true">
        Vorderseite
      </button>
      <button type="button" class="ap-product-view-switcher__button" data-ap-view="back" aria-pressed="false">
        Rückseite
      </button>
    `;

    switcher.addEventListener('click', async event => {
      const button = event.target.closest('[data-ap-view]');
      if (!button || AP.generating) {
        return;
      }

      await setView(button.dataset.apView, true);
    });

    galleryViewer.insertAdjacentElement('afterend', switcher);
    return switcher;
  }

  function updateSwitcher() {
    const desktopSwitcher = ensureSwitcher();
    const mobileSwitcher = ensureMobileSwitcher();

    const mode = getPrintMode();
    const visible = mode === 'both';

    if (desktopSwitcher) {
      desktopSwitcher.hidden = !visible;
    }

    if (mobileSwitcher) {
      mobileSwitcher.hidden = !visible;
    }

    [desktopSwitcher, mobileSwitcher]
      .filter(Boolean)
      .forEach(switcher => {
        switcher.querySelectorAll('[data-ap-view]').forEach(button => {
          const active = button.dataset.apView === AP.view;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });
  }

  async function syncFromSelections() {
    if (AP.generating) return;

    const mode = getPrintMode();

    if (mode === 'front') {
      AP.view = 'front';
      AP.userView = 'front';
    } else if (mode === 'back') {
      AP.view = 'back';
      AP.userView = 'back';
    } else if (mode === 'both') {
      AP.view = AP.userView || 'front';
    } else {
      AP.view = 'front';
      AP.userView = 'front';
    }

    installBackgroundGuard();
    installMotifPlaceholderGuards();
    applyCustomMotifPlaceholders();

    await setView(AP.view, false);

    /*
     * Falls Easify beim Setzen der Ansicht gerade Nodes neu initialisiert hat,
     * den eigenen Platzhalter noch einmal ereignisbasiert anwenden.
     */
    installMotifPlaceholderGuards();
    applyCustomMotifPlaceholders();

    updateSwitcher();
  }

  async function stageToFile(filename) {
    const stage = getStage();
    if (!stage) {
      throw new Error('Keine Konva Stage gefunden.');
    }

    const dataUrl = stage.toDataURL({ pixelRatio: 2 });
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    return new File([blob], filename, { type: 'image/png' });
  }

  function removePreview(form, side) {
    form.querySelectorAll(`[data-ap-generated-preview="${side}"]`).forEach(input => input.remove());
  }

  function attachPreview(form, side, label, file) {
    removePreview(form, side);

    const input = document.createElement('input');
    input.type = 'file';
    input.hidden = true;
    input.name = `properties[${label}]`;
    input.dataset.apGeneratedPreview = side;

    const dt = new DataTransfer();
    dt.items.add(file);
    input.files = dt.files;

    form.appendChild(input);
    form.enctype = 'multipart/form-data';
  }

  async function prepareSnapshotView(view) {
    const color = getSelectedColor();
    if (!color) {
      throw new Error('Für die Vorschau konnte keine Farbe ermittelt werden.');
    }

    const wantedUrl = findProductImage(color, view);
    if (!wantedUrl) {
      throw new Error(`Kein ${view}-Produktbild für ${color} gefunden.`);
    }

    AP.view = view;
    showOnlyMotif(view);

    for (let attempt = 1; attempt <= 5; attempt++) {
      await loadBackground(wantedUrl);
      showOnlyMotif(view);
      await wait(60);

      const parts = getKonvaParts();
      if (!parts) {
        throw new Error('Easify/Konva ist nicht mehr verfügbar.');
      }

      const currentUrl = parts.background.image()?.src || '';
      const wantedFile = decodeURIComponent(wantedUrl).split('?')[0].toLowerCase();
      const currentFile = decodeURIComponent(currentUrl).split('?')[0].toLowerCase();

      if (currentFile === wantedFile) {
        await new Promise(resolve =>
          requestAnimationFrame(() => requestAnimationFrame(resolve))
        );

        const finalParts = getKonvaParts();
        const finalUrl = finalParts?.background?.image()?.src || '';
        const finalFile = decodeURIComponent(finalUrl).split('?')[0].toLowerCase();

        if (finalFile === wantedFile) {
          showOnlyMotif(view);
          finalParts.background.getLayer()?.batchDraw();
          finalParts.liveLayer?.batchDraw();

          console.log(`[Aus-PRINTzip] Snapshot bereit: ${view}`, finalUrl);
          return;
        }
      }

      console.warn(
        `[Aus-PRINTzip] Easify hat ${view} erneut überschrieben – Versuch ${attempt}/5`
      );
    }

    throw new Error(`Der ${view}-Hintergrund konnte für den Snapshot nicht stabil gesetzt werden.`);
  }

  async function createOrderPreviews(form) {
    const mode = getPrintMode();

    if (!['front', 'back', 'both'].includes(mode)) {
      return;
    }

    const restoreView = AP.userView || AP.view || 'front';
    const parts = getKonvaParts();

    if (!parts) {
      throw new Error('Easify Vorschau nicht verfügbar.');
    }

    const transformerVisible = parts.transformerLayer
      ? parts.transformerLayer.visible()
      : null;

    if (parts.transformerLayer) {
      parts.transformerLayer.visible(false);
      parts.transformerLayer.batchDraw();
    }

    AP.generating = true;

    try {
      removePreview(form, 'front');
      removePreview(form, 'back');

      if (mode === 'front' || mode === 'both') {
        await prepareSnapshotView('front');

        const frontFile = await stageToFile('vorschau-vorderseite.png');
        attachPreview(form, 'front', 'Vorschau Vorderseite', frontFile);
      }

      if (mode === 'back' || mode === 'both') {
        await prepareSnapshotView('back');

        const backFile = await stageToFile('vorschau-rueckseite.png');
        attachPreview(form, 'back', 'Vorschau Rückseite', backFile);
      }
    } finally {
      if (parts.transformerLayer && transformerVisible !== null) {
        parts.transformerLayer.visible(transformerVisible);
        parts.transformerLayer.batchDraw();
      }

      if (mode === 'front') {
        AP.view = 'front';
      } else if (mode === 'back') {
        AP.view = 'back';
      } else {
        AP.view = restoreView;
      }

      AP.generating = false;
      await setView(AP.view, false);
    }
  }

  document.addEventListener(
    'submit',
    async event => {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) {
        return;
      }

      if (!form.action.includes('/cart/add')) {
        return;
      }

      if (!form.closest('product-info')) {
        return;
      }

      if (AP.allowSubmit) {
        AP.allowSubmit = false;
        return;
      }

      const mode = getPrintMode();
      if (!mode || mode === 'none') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const submitter = event.submitter || form.querySelector('[name="add"]');

      if (submitter) {
        submitter.disabled = true;
        submitter.classList.add('ap-preview-generating');
      }

      try {
        await createOrderPreviews(form);
        AP.allowSubmit = true;

        if (submitter) {
          submitter.disabled = false;
          submitter.classList.remove('ap-preview-generating');
          form.requestSubmit(submitter);
        } else {
          form.requestSubmit();
        }
      } catch (error) {
        console.error('[Aus-PRINTzip] Vorschau konnte nicht erstellt werden:', error);

        if (submitter) {
          submitter.disabled = false;
          submitter.classList.remove('ap-preview-generating');
        }

        alert('Die Druckvorschau konnte nicht erstellt werden. Bitte versuche es erneut.');
      }
    },
    true
  );

  function scheduleSync(delay = 180) {
    if (AP.generating) {
      return;
    }

    clearTimeout(AP.updateTimer);
    AP.updateTimer = setTimeout(syncFromSelections, delay);
  }

  document.addEventListener(
    'change',
    event => {
      if (AP.generating) {
        return;
      }

      const target = event.target;

      if (target instanceof HTMLInputElement && target.type === 'file') {
        setTimeout(() => scheduleSync(0), 500);
        setTimeout(() => scheduleSync(0), 1200);

        setTimeout(() => {
          installMotifPlaceholderGuards();
          applyCustomMotifPlaceholders();
          hideBackGalleryImages();
        }, 1300);

        return;
      }

      clearProductImageCache();

      /*
       * Easify aktualisiert _tpo_custom_options leicht verzögert.
       * Deshalb zwei ereignisbasierte Syncs, aber KEIN Dauer-Polling.
       */
      scheduleSync(160);
      setTimeout(() => scheduleSync(0), 420);

      setTimeout(hideBackGalleryImages, 300);
    },
    true
  );

  document.addEventListener(
    'input',
    event => {
      if (AP.generating) {
        return;
      }

      const target = event.target;

      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        scheduleSync(120);
      }
    },
    true
  );


  function getEasifyOptionsInput() {
    return (
      document.querySelector('#_tpo_custom_options') ||
      document.querySelector(
        'input[name="properties[_tpo_custom_options]"]'
      )
    );
  }

  function startEasifyOptionsWatcher() {
    if (AP.easifyOptionsWatcher) {
      return;
    }

    const readCurrentValue = () => {
      const input = getEasifyOptionsInput();
      return input?.value || '';
    };

    AP.lastEasifyOptionsValue = readCurrentValue();

    AP.easifyOptionsWatcher = window.setInterval(() => {
      if (AP.generating) {
        return;
      }

      const currentValue = readCurrentValue();

      if (
        currentValue === AP.lastEasifyOptionsValue
      ) {
        return;
      }

      AP.lastEasifyOptionsValue = currentValue;

      /*
       * Easify hat eine Option geändert.
       * Jetzt genau EINMAL synchronisieren.
       *
       * Wichtig:
       * Kein permanentes Background-/Canvas-Polling.
       */
      clearProductImageCache();
      scheduleSync(0);

      setTimeout(() => {
        installMotifPlaceholderGuards();
        applyCustomMotifPlaceholders();
        hideBackGalleryImages();
      }, 250);
    }, 200);
  }

  async function init() {
    injectStyles();

    for (let i = 0; i < 30; i++) {
      if (getStage()) {
        break;
      }

      await wait(200);
    }

    clearProductImageCache();
    hideBackGalleryImages();

    installBackgroundGuard();
    installMotifPlaceholderGuards();
    applyCustomMotifPlaceholders();

    ensureSwitcher();
    ensureMobileSwitcher();

    startEasifyOptionsWatcher();

    await syncFromSelections();

    console.log(
      '[Aus-PRINTzip] Front-/Back-System geladen.',
      AP_VERSION,
      'PrintMode:',
      getPrintMode()
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

document.addEventListener('change', event => {
  if (window.innerWidth > 749) return;

  const target = event.target;

  if (!(target instanceof HTMLInputElement)) return;
  if (target.type !== 'radio') return;

  const fieldset = target.closest('fieldset');
  if (!fieldset) return;

  const legend = fieldset.querySelector('legend');
  const legendText = (legend?.textContent || '').toLowerCase();

  if (!legendText.includes('farbe')) return;

  const product = target.closest('product-info');
  if (!product) return;

  const gallery =
    product.querySelector('media-gallery') ||
    product.querySelector('[id^="GalleryViewer-"]');

  if (!gallery) return;

  setTimeout(() => {
    gallery.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, 250);
});